import { ResetPasswordRequestData, AuthState, ActivateAccountData, ResetPasswordData, SignUpData, LoginData, LoggedInState, PrivacyKind, ResendActivationData, BasicCredentials, User, defaultAuthState } from './authState';
import { Widget, promise, nothing, Updater } from 'widgets-for-react'
import { ActivateAccountResult, ChangePasswordResult, LoginResult, ResendActivationResult, ResetPasswordRequestResult, ResetPasswordResult, SignUpResult, TwoFactorResult } from './apiTypes';
import { Option } from 'widgets-for-react'
import { PasswordScoreTypes } from './authenticationApi';
import { routeUpdaters } from '../../site/root/routes/routesState';
import i18next from 'i18next';
import { DataLayer } from 'utils/datalayer';

export type Api = {
  request_reset_password : (email: string) => Promise<ResetPasswordRequestResult>
  resend_account_activation : (x: ResendActivationData, originPath: string) => Promise<ResendActivationResult>
  activate_account : (aad:ActivateAccountData) => Promise<ActivateAccountResult>
  activate_account_with_reset : (aad:ActivateAccountData) => Promise<ActivateAccountResult | ChangePasswordResult>
  reset_password : (x: ResetPasswordData) => Promise<ResetPasswordResult>
  sign_up : (x: SignUpData, originPath: string) => Promise<SignUpResult>
  login : (x: BasicCredentials & {Role?: string}, useTwoFactor?: string | false) => Promise<LoginResult>
  request_new_pin : (x: LoginData) => Promise<TwoFactorResult>
  logout : (u: User, s: string) => Promise<{}>
  scorePassword: (i:string) =>  {score:number, res: PasswordScoreTypes}
  isSessionValid : () => Promise<Option<LoginResult>>
}

export let reset_password_request =  (api : Api, rprd:ResetPasswordRequestData) : Widget<Updater<AuthState>> =>
  api.request_reset_password == undefined ? nothing() :
  promise<{}, Updater<AuthState>>(_ => api.request_reset_password(rprd.email!).then<Updater<AuthState>>(res =>
    (s:AuthState) : AuthState =>
      res.status == "success" ? {...s, resetPasswordRequestState:"reset password request - success", login_data: rprd }
      : res.status == "error email not valid" ? {...s, resetPasswordRequestState:"reset password request - error email not valid", login_data:rprd }
      : res.status == "error email not found" ? {...s, resetPasswordRequestState:"reset password request - error email not found", login_data: rprd }
      : res.status == "error account not activated" ? {...s, resetPasswordRequestState:"reset password request - error user not activated", login_data:rprd }
      : res.status == "error too many attempts" ? {...s, resetPasswordRequestState: "reset password request - error too many attempts", login_data: rprd }
      : {...s, resetPasswordRequestState:"reset password request - error email not found", login_data:rprd }),
    { key: "login-widget-reset-password-request-promise", on_fail:() => (s => ({...s, resetPasswordRequestState:"reset password request - api error", login_data:rprd })) }
  )({})

export let resend_account_activation = (api : Api, rad: ResendActivationData, originPath: string): Widget<Updater<AuthState>> =>
    api.resend_account_activation == undefined ? nothing() :
    promise<{}, Updater<AuthState>>(_ => api.resend_account_activation(rad, originPath).then<Updater<AuthState>>( res =>
      (s:AuthState): AuthState =>
        res.status == "success" ? {...s,  resendActivationState: "resend activation - success", login_data: rad }
        : res.status == "error too many attempts" ? { ...s,  resendActivationState: "resend activation - error too many attempts", login_data: rad }
        : { ...s,  resendActivationState: "resend activation - api error", login_data: rad }
      ),
      { on_fail:() => s => ({...s,  resendActivationState:"resend activation - api error", login_data: rad}) }
    )({})

export let activate_account = (api : Api, aad:ActivateAccountData) : Widget<Updater<AuthState>> =>
  api.activate_account == undefined ? nothing() :
  promise<{}, Updater<AuthState>>(_ => api.activate_account(aad).then<Updater<AuthState>>(res =>
    (s:AuthState) : AuthState => res.status == "success" ? {...s, activateAccountState: "activate account - account activated", status:"logged in", loggedInState: 'logged in', user: res.value, session: res.session, role: res.role } :
      res.status === "error account activation failed" ? {... s, activateAccountState: "activate account - error account activation failed"} :
      res.status == "change password required" ? {...s, activateAccountState: "activate account - change password"} 
      : res.status == "token expired" ? {...s,  activateAccountState: "activate account - error token expired" }
      : res.status == "account already active" ? {...s,  activateAccountState: "activate account - account activated"}
      : res.status == "token not found" ? {...s, activateAccountState: "activate account - error token not found" }
      : res.status == "error too many attempts" ? {...s, activateAccountState: "activate account - api error" }
      : {...s, activateAccountState:"activate account - api error" }
    ),
    { key: "login-widget-activate-account-promise", on_fail:() => (s => ({...s, activateAccountState:"activate account - api error", activateAccountData:aad })) }
  )({})

export let activate_and_change_password = (api : Api, aad:ActivateAccountData) : Widget<Updater<AuthState>> =>
  api.activate_account == undefined ? nothing() :
  promise<{}, Updater<AuthState>>(_ => api.activate_account_with_reset(aad).then<Updater<AuthState>>(res =>
    (s:AuthState) : AuthState => {
      if (res.status == "success") {
        return {...s, status: 'logged in', activateAccountState: "activate account - account activated" }
      }
      return res.status == "error missing fields" ? {...s, activateAccountState: "activate account - change password"}
      : res.status == "error passwords do not match" ? {...s, activateAccountState: "activate account - change password"} 
      : res.status == "error too weak password" ? {...s, activateAccountState: "activate account - error weak password"}
      : res.status == "token expired" ? {...s,  activateAccountState: "activate account - error token expired" }
      : res.status == "account already active" ? {...s,  activateAccountState: "activate account - account activated"}
      : res.status == "token not found" ? {...s, activateAccountState: "activate account - error token not found" }
      : res.status == "error too many attempts" ? {...s, activateAccountState: "activate account - api error" }
      : {...s, activateAccountState:"activate account - api error" }
    }),
    { key: "login-widget-activate-account-promise", on_fail:() => (s => ({...s, activateAccountState:"activate account - api error", activateAccountData:aad })) }
  )({})

export let reset_password = (api : Api, rpd:ResetPasswordData) : Widget<Updater<AuthState>> =>
 { 
  return api.reset_password == undefined ? nothing() :
  promise<{}, Updater<AuthState>>(_ => api.reset_password(rpd).then<Updater<AuthState>>(res =>
    (s:AuthState) : AuthState =>
      ( res.status == "success" ? {...s, resetPasswordState:"reset password success" }
      : res.status == "error too many attempts" ? {...s, resetPasswordState:"reset password - error too many attempts" }
      : res.status == "error too weak password" ? {...s, resetPasswordState:"reset password - error weak password" }
      : res.status == "error token not valid" ? {...s, resetPasswordState:"reset password - error token not valid" }
      : {...s, resetPasswordState:"reset password - api error"})),
    { key: "login-widget-reset-password-promise", on_fail:() => (s => ({...s, resetPasswordState:"reset password - api error" })) }
  )({})}

export let sign_up = (api : Api, sud:SignUpData, security_kind: PrivacyKind, originPath: string) : Widget<Updater<AuthState>> =>
  api.sign_up == undefined ? nothing() :
  promise<{}, Updater<AuthState>>(_ => api.sign_up(sud, originPath).then<Updater<AuthState>>((res: SignUpResult) =>
    (s:AuthState) : AuthState => {
      
      if(res.status === 'success') {
        DataLayer.newTrialAccountCreated(res.user_id);
      }

      return res.status == "success" ? {...s, signUpState:"sign up - success"}
      : res.status == "too weak password" ? {...s, signUpState: "sign up - error weak password", login_data: sud}
      : res.status == "email invalid" ? {...s, signUpState: "sign up - error invalid email", login_data: sud}
      : (res.status == "username invalid" && security_kind == "strict" && console.warn("Inconsistent api result: this might lead to a privacy issue") == undefined) ||
         res.status == "username invalid" ? {...s, signUpState: "sign up - error invalid username", login_data: sud}
      : (res.status == "account already exists" && security_kind == "strict" && console.warn("Inconsistent api result: this might lead to a privacy issue") == undefined) ||
         res.status == "account already exists" ? {...s, signUpState: "sign up - error account already exists", login_data: sud}
      : (res.status == "account not active" && security_kind == "strict" && console.warn("Inconsistent api result: this might lead to a privacy issue") == undefined) ||
         res.status == "account not active" ? {...s, signUpState: "sign up - error account not active", login_data: sud}
      : res.status == "error too many attempts" ? {...s, signUpState: "sign up - error too many attempts", login_data: sud}
      : res.status == "error invalid promo code" ? {...s, signUpState: "sign up - error invalid promo code", login_data: sud}
      : {...s, signUpState:"sign up - api error", login_data:sud }}
    ),
    { on_fail:() => (s => ({...s, signUpState:"sign up - api error", login_data:sud })) }
  )({})

export let isSessionValid = (api: Api): Widget<Updater<AuthState>> => {
  return api.login == undefined ? nothing() :
  promise<{}, Updater<AuthState>>(_ => api.isSessionValid().then<Updater<AuthState>>(loginResult =>
    (s:AuthState) : AuthState => {
      if (loginResult.kind == "r" && loginResult.v.status == "success") {
        return {...s, 
          status: "logged in", 
          user: loginResult.v.user, 
          session: loginResult.v.session,
          role: loginResult.v.role
        }
      }
      return {...s, 
        status: 'logged out'
      }
    }),
    { key: "login-widget-login-promise", on_fail:() => s => ({...s, loggedOutState:"logged out" }) }
 )({})
}

export let login = (api : Api, ld:LoginData, security_kind:PrivacyKind) : Widget<Updater<AuthState>> =>{
  return api.login == undefined ? nothing() :
  promise<{}, Updater<AuthState>>(_ => api.login(ld).then<Updater<AuthState>>(res =>
    (s:AuthState) : AuthState => res.status == "success" ? {...s, status:"logged in", loggedOutState: 'logged in - redirecting', user:res.user, session:res.session, role: res.role }
      : ((security_kind == "strict" &&  res.status == "error account not active" && console.warn("Inconsistent api result: this might lead to a privacy issue") == undefined)) ||
        security_kind == "standard" && res.status == "error account not active" ? {...s,loggedOutState:"logged out - error account not active", login_data:{...ld, email: res.email} }
      : res.status == "proceed with two factor" ? {...s, loggedOutState:"two factor", login_data:{...ld, pin:""} }
      : res.status == "error too many attempts" ? {...s, loggedOutState:"logged out - error too many attempts", login_data:ld }
      : res.status == "error account blocked" ? {... s, loggedOutState:"logged out - error account blocked"}
      : res.status == "error generic not authorized" ? {...s, loggedOutState:"logged out - error login not authorized"}
      : res.status == "must reset password" ? {...s, loggedOutState: "logged out - must reset password"}
      : {...s, loggedOutState:"logged out - error login not authorized", login_data:ld }
    ),
    { key: "login-widget-login-promise", on_fail:() => s => ({...s,loggedOutState:"logged out - login api error", login_data:ld }) }
 )({}) }

export let send_new_pin = (api : Api, ld:LoginData) : Widget<Updater<AuthState>> =>
  api.request_new_pin == undefined ? nothing() :
  promise<{}, Updater<AuthState>>(_ => api.request_new_pin(ld).then<Updater<AuthState>>(res =>
    (s:AuthState) : AuthState =>
      (res.status == "success" ?  {...s, loggedOutState:"two factor", login_data:ld }
      : res.status == "error too many attempts" ?  {...s, loggedOutState:"two factor - error too many attempts", login_data:ld }
      : {...s, loggedOutState:"logged out - error login not authorized", login_data:{show_password:false, password:ld.password, email:ld.email} })),
    { on_fail:() => s => ({...s, loggedOutState:"two factor - resend api error", login_data:{...ld, pin:""} }) }
  )({})

export let login_two_factor = (api : Api, ld:LoginData) : Widget<Updater<AuthState>> =>
  api.login == undefined ? nothing() :
  promise<{}, Updater<AuthState>>(_ => api.login(ld, ld.pin).then<Updater<AuthState>>(res =>
    (s:AuthState) : AuthState => {
      if (res.status == "success") {
        // setSession(res.user, res.session, res.role)
        return {...defaultAuthState, status:"logged in", user:res.user, session:res.session, role: res.role }
      }
      return res.status == "error two factor empty code" ? {...s, loggedOutState:"two factor - error empty code", login_data:ld}
      : res.status == "error too many attempts" ? {...s, loggedOutState:"two factor - error too many attempts", login_data:ld}
      : res.status == "error two factor wrong code" ? {...s, loggedOutState:"two factor - error wrong code", login_data:ld }
      // : res.status == "choose role" ? {...s, status:"choose role", login_data: {...ld, roles: res.roles}}
      : {...s, loggedOutState:"logged out - error login not authorized", login_data:{email: ld.email, password: ld.password, show_password: false} }
    }),
    { key: "login-widget-login-two-factor-promise", on_fail:() => (s => ({...s, loggedOutState:"two factor - login api error", login_data:ld })) }
  )({})

export let logout = (api : Api, s0:LoggedInState) : Widget<Updater<AuthState>> => {
  return api.logout == undefined ? nothing() :
  promise<{}, Updater<AuthState>>(_ => api.logout(s0.user, s0.session).then<Updater<AuthState>>(() =>
    () : AuthState => {
      window.location.href = routeUpdaters(i18next.language).home.url
      return defaultAuthState
    }),
    { key: "login-widget-logout-promise", on_fail:() => s => ({...s, loggedInState:"logged in - logout api error", user:s0.user, session:s0.session, role: s0.role }) }
  )({})
}

